import { GAMEWITH_HOSTS, type KeywordResult } from '../types';

/** Bare lowercase host from operator input ("https://www.Example.com/x" →
 *  "example.com"), or null when it isn't a plausible host. Mirrors
 *  functions/src/utils/domain.ts normalizeDomain. */
export function normalizeDomain(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;
  let host: string;
  try {
    host = new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`).hostname;
  } catch {
    return null;
  }
  host = host.replace(/\.$/, '').replace(/^www\./, '');
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host)) return null;
  return host;
}

/** `host` is `domain` or a subdomain of it. */
export function hostMatchesDomain(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

export function isGamewithDomain(domain: string): boolean {
  return GAMEWITH_HOSTS.some((h) => hostMatchesDomain(domain, h));
}

/**
 * A competitor's position for one row. Prefers what the run recorded; when
 * the domain was added after the run, falls back to scanning the stored
 * topResults (top 10 only — the functions side ranks from the full organic
 * list) so a newly configured domain shows up without waiting for the next
 * run; the next run replaces it with the recorded value.
 */
export function competitorRank(r: KeywordResult, domain: string): number | null {
  const stored = r.competitorRankings?.[domain];
  if (stored !== undefined) return stored;
  for (const t of r.topResults ?? []) {
    try {
      if (hostMatchesDomain(new URL(t.url).hostname.toLowerCase(), domain)) return t.position;
    } catch {
      /* malformed url, skip */
    }
  }
  return null;
}
